import { defineStore } from 'pinia';
import { nextTick } from 'vue'; // 新增导入 nextTick

// 这是一个包含了所有可配置参数及其默认值的对象
const defaultSceneParams = {
  // --- 相机控制 ---
  cameraType: 'Perspective', // 'Orthographic' 或 'Perspective'
  perspectiveFov: 75,         // 透视相机: 视野
  perspectiveZ: 80,           // 透视相机: Z轴位置

  // --- 几何与布局 ---
  PLANE_COUNT_X: 35,
  PLANE_COUNT_Y: 20,
  SCROLL_SPEED: 0.0,
  planeDepthSpacing: 0.1,     // 新增: 面片在Z轴上的间距
  planeVisualHeight: 6.0,     // 新增: 面片的视觉高度

  // --- "山脉" 形态效果 ---
  BULGE_AMOUNT: 10.0,
  BULGE_SCALE_AMOUNT: 0.6,
  
  // --- "波浪" 动画效果 ---
  IDLE_ANIM_FREQUENCY: 0.8,
  IDLE_ANIM_AMPLITUDE: 3.0,

  // --- 鼠标交互效果 ---
  MOUSE_RIPPLE_AMOUNT: 6.0,
  MOUSE_RIPPLE_SCALE_AMOUNT: 1.0,
  HOVER_SCALE_FACTOR: 1.4,
  HOVER_Z_LIFT: 25.0,

  // --- 动画与过渡 ---
  LERP_FACTOR: 0.025,
  MOUSE_SMOOTHING_FACTOR: 0.6,
  TRANSITION_DURATION: 1200,

  // --- 状态切换周期 (秒) ---
  STATE_CYCLE_DURATION: 12.0,
  WAVE_STATE_DURATION: 4.0,
  FADE_IN_SPEED: 0.2,
  FADE_OUT_SPEED: 0.1,

  // --- 片元着色器 (Fragment Shader) ---
  shader_blend_mode: 'Normal',
  shader_trail_factor: 4.0,
  shader_top_color_multiplier: 2.5,
  shader_bottom_color_multiplier: 0.6,
  shader_global_alpha: 0.8,
  
  // --- 【新增】顶点着色器 (Vertex Shader) ---
  shader_vertex_slant: 0.005,  // 梯形倾斜度
  shader_vertex_squash: 0.5,   // 形变/压扁程度
  shader_vertex_size: 0.6,     // 基础大小影响
};

export const useMainStore = defineStore('main', {
  state: () => ({
    allElements: [],
    loading: true,
    error: null,
    
    // --- REFACTORED STATE ---
    hoveredColor: null,
    selectedColor: null,

    // --- Filter State ---
    activeSeasons: new Set(['春', '夏', '秋', '冬']), // 保持与 FilterPanel.vue 的默认值同步
    altitudeRange: [0, 6100],
    activeCategories: new Set(),
    
    // --- NEW STATE for controlling animation dynamics ---
    animationIntensity: 0.5, // Default value (0 to 1)
    sceneParams: { ...defaultSceneParams },
    details: [],  // 存放所有打开的详情项
     // 新增：使用一个对象来管理DebugPanel的完整状态
    debugPanel: {
      isVisible: false,
      isCentered: false,
      position: { top: '0px', left: '0px' },
    },
    // === 新增 State ===
    isTravelMapVisible: false,
    activeTravelMapColor: null,

    sceneCanvas: null, // 【新增】用于存储 Three.js Canvas 元素
    isExporting: false, // 【新增】标记是否正在导出图片
    scene: null, // 【新增】存储 THREE.Scene 实例
    renderer: null, // 【新增】存储 THREE.WebGLRenderer 实例
    camera: null,
    activeDebugSection: null // 暂时用作其他用途，但不是必须的
  }),
  // in mainStore.js

  getters: {
    /**
     * The core getter. Flattens colors and applies all filters.
     * 【重要修改】移除了按海拔排序的逻辑。
     */
    colorPoints: (state) => {
      if (state.loading) return [];
      
      const points = [];
      state.allElements.forEach(element => {
        if (element.full_colors) {
          element.full_colors.forEach(color => {
            points.push({
              id: `${element.id}-${color.id}`,
              color: color,
              element: element,
            });
          });
        }
      });

      const hasActiveCategories = state.activeCategories.size > 0;
      const hasActiveSeasons = state.activeSeasons.size > 0;


      // 过滤逻辑保持不变，确保 UI 筛选功能正常
      const filteredPoints = points.filter(point => {
        const el = point.element;

        // 【修正】确保在没有激活任何分类时，季节筛选对所有元素都有效
        const isCulturalOnly = hasActiveCategories && [...state.activeCategories].every(cat => ['建筑', '服饰', '器物', '日用', '壁画'].includes(cat));

         const seasonMatch = isCulturalOnly || !hasActiveSeasons || state.activeSeasons.has(el.full_season?.name);

        const altitudeMatch = el.full_location && 
                              (el.full_location.altitude >= state.altitudeRange[0] && 
                               el.full_location.altitude <= state.altitudeRange[1]);
        const categoryMatch = !hasActiveCategories || state.activeCategories.has(el.full_category?.name);

        return seasonMatch && altitudeMatch && categoryMatch;
      });

      // 【重要修改】返回纯粹的、经过筛选但未排序的数组
      return filteredPoints;
    },
  },
  
  actions: {

    // 【新增】注册场景和渲染器
    setSceneRendererAndCamera(scene, renderer, currentCamera) {
      this.scene = scene;
      this.renderer = renderer;
      this.camera = currentCamera;
    },
    
     // 【新增】设置 Canvas 实例
    setCanvas(canvas) {
      this.sceneCanvas = canvas;
    },

    // 【新增】导出场景为 PNG 图片
    async exportSceneAsPNG() {
      if (!this.sceneCanvas) {
        console.error('Canvas element not found.');
        return;
      }

      // 1. 标记导出状态，隐藏所有UI浮层
      this.isExporting = true;
      
      // 2. 等待 DOM 更新，确保所有UI元素已经隐藏
      await nextTick();

      try {
        // 3. 捕捉 Canvas 内容并生成下载链接
        const dataURL = this.sceneCanvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = dataURL;
        link.download = '稻城色韵-色彩图谱.png'; // 设定文件名
        
        // 4. 触发下载
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Failed to export scene as PNG:', error);
      } finally {
        // 5. 恢复UI的显示
        this.isExporting = false;
      }
    },
    async initDatabase() {
      this.loading = true;
      this.error = null;
      try {
        const response = await fetch('/daocheng_yading_database_v2.json');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const db = await response.json();
        
        const locationsByIdMap = new Map(Object.values(db.locations).map(loc => [loc.id, loc]));
        const categoriesByIdMap = new Map(Object.values(db.categories).map(cat => [cat.id, cat]));
        const seasonsByIdMap = new Map(Object.values(db.seasons).map(sea => [sea.id, sea]));
        const colorsByIdMap = new Map(Object.values(db.colors).map(c => [c.id, c]));

        const elements = Object.values(db.elements).map(element => ({
          ...element,
          full_location: locationsByIdMap.get(element.location_id),
          full_category: categoriesByIdMap.get(element.category_id),
          full_season: seasonsByIdMap.get(element.season_id),
          full_colors: (element.color_ids || []).map(cid => colorsByIdMap.get(cid)).filter(Boolean),
        }));
        
        this.allElements = elements;
        this.loading = false;
      } catch (error) {
        this.loading = false;
        this.error = error.message;
      }
    },
    
    setHoveredColor(colorPoint) {
      this.hoveredColor = colorPoint;
    },
    
    setSelectedColor(colorPoint) {
      if (this.selectedColor && colorPoint && this.selectedColor.id === colorPoint.id) {
        this.selectedColor = null;
      } else {
        this.selectedColor = colorPoint;
      }
    },
    
    setActiveSeasons(seasons) { this.activeSeasons = seasons; this.selectedColor = null; },
    setAltitudeRange(range) { this.altitudeRange = range; this.selectedColor = null; },
    toggleCategory(category) {
      if (this.activeCategories.has(category)) {
        this.activeCategories.delete(category);
      } else {
        this.activeCategories.add(category);
      }
      this.selectedColor = null;
    },

    // --- NEW ACTION for controlling animation dynamics ---
    setAnimationIntensity(intensity) {
      this.animationIntensity = intensity;
    },
    // 【新增】重置场景参数的 action
    resetSceneParams() {
      this.sceneParams = { ...defaultSceneParams };
    },
    /** 打开一个详情面板 */
    addDetail(detail) {
      // detail = { id, thumbnailSrc, highResSrc, title, description }
      this.details.push({
        ...detail,
        loadedHighRes: false
      });
    },
    /** 关闭索引为 index 的面板 */
    removeDetail(index) {
      this.details.splice(index, 1);
    },
     // 新增：显示DebugPanel的方法，可以接收配置
    showDebugPanel(options = { centered: false, position: null }) {
      this.debugPanel.isCentered = options.centered;
      if (options.position) {
        this.debugPanel.position = options.position;
      }
      this.debugPanel.isVisible = true;
    },

    // 新增：隐藏DebugPanel的方法
    hideDebugPanel() {
      this.debugPanel.isVisible = false;
    },
    // === 新增 Actions ===
    showColorTravelMap() { // <--- 注意：这里不再关心传入的参数
      console.log("正在强制打开地图组件进行调试...");

      // 创建一个写死的、保证有效的假数据对象
      const debugColorData = {
        hex: '#BF3F3F', // 一个醒目的颜色
        name: '调试专用色',
        element: {
          name: '默认地点',
          altitude: 3000,
          location: {
            name: '稻城亚丁',
            lng: 100.3519,
            lat: 28.3728,
          }
        }
      };
      
      this.activeTravelMapColor = debugColorData; // 使用假数据
      this.isTravelMapVisible = true; // 强制显示地图
    },
    
    hideColorTravelMap() {
      this.isTravelMapVisible = false;
      // 延迟清空数据，让过渡动画更平滑
      setTimeout(() => {
        this.activeTravelMapColor = null;
      }, 500);
    },
  // 在 mainStore.js 的 actions: { ... } 中
applyScenePreset(presetKey) {
      // 切换预设前，先重置所有参数，确保是一个干净的状态
      this.resetSceneParams();

      // 根据传入的 key 来应用不同的参数配置
      switch (presetKey) {
        case 'default':
          // '默认' 模式，就是重置后的状态
          break;

        case 'dynamic':
          // 对应你提供的第二种模式: "3d透视 50，90..."
          this.sceneParams.cameraType = 'Perspective';
          this.sceneParams.perspectiveFov = 50;
          this.sceneParams.perspectiveZ = 90;
          this.sceneParams.planeVisualHeight = 1.0;
          this.sceneParams.PLANE_COUNT_Y = 32;
          this.sceneParams.SCROLL_SPEED = 2.0;
          this.sceneParams.BULGE_AMOUNT = 20.0;
          this.sceneParams.IDLE_ANIM_FREQUENCY = 1.5;
          this.sceneParams.IDLE_ANIM_AMPLITUDE = 6.0;
          this.sceneParams.shader_trail_factor = 10.0;
          this.sceneParams.shader_global_alpha = 1.0;
          break;
        
        case 'soft':
          // 对应你提供的第三种模式: "3d透视 55 90..."
          this.sceneParams.cameraType = 'Perspective';
          this.sceneParams.perspectiveFov = 55;
          this.sceneParams.perspectiveZ = 90;
          this.sceneParams.SCROLL_SPEED = 0.65;
          this.sceneParams.planeVisualHeight = 1.0;
          this.sceneParams.PLANE_COUNT_Y = 40;
          this.sceneParams.IDLE_ANIM_AMPLITUDE = 2.0;
          this.sceneParams.IDLE_ANIM_FREQUENCY = 1.2;
          this.sceneParams.BULGE_AMOUNT = 35.5;
          this.sceneParams.shader_vertex_slant = 0.02;
          this.sceneParams.shader_vertex_squash = 2.0;
          this.sceneParams.shader_vertex_size = 1.25;
          this.sceneParams.shader_trail_factor = 6.0;
          this.sceneParams.shader_top_color_multiplier = 4.5;
          this.sceneParams.shader_global_alpha = 1.0;
          break;

        case 'calm':
          // 对应你提供的第一种模式: "2d正交..."
          this.sceneParams.cameraType = 'Orthographic';
          this.sceneParams.PLANE_COUNT_X = 39;
          this.sceneParams.PLANE_COUNT_Y = 17;
          this.sceneParams.SCROLL_SPEED = 1.10;
          this.sceneParams.planeVisualHeight = 9.5;
          this.sceneParams.BULGE_AMOUNT = 16.5;
          this.sceneParams.IDLE_ANIM_FREQUENCY = 1.5;
          this.sceneParams.IDLE_ANIM_AMPLITUDE = 9.0;
          this.sceneParams.shader_vertex_slant = 0.0058;
          this.sceneParams.shader_vertex_squash = 1.15;
          this.sceneParams.shader_vertex_size = 0.0;
          this.sceneParams.shader_trail_factor = 4.0;
          this.sceneParams.shader_top_color_multiplier = 1.0;
          this.sceneParams.shader_bottom_color_multiplier = 1.5;
          this.sceneParams.shader_global_alpha = 1.0;
          break;
      }
    },
  },
});